/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from "react";

export interface Question {
  id: string;
  question: string;
  options: string[];
  correctIndex: number;
  explanation: string;
}

interface ExamContextType {
  questions: Question[];
  currentIndex: number;
  selectedAnswers: Record<number, number>;
  remainingSeconds: number;
  isExamActive: boolean;
  isExamFinished: boolean;
  examClass: string;
  startExam: (questions: Question[], className: string) => void; // Interface requires className
  selectAnswer: (optionIndex: number) => void;
  nextQuestion: () => void;
  prevQuestion: () => void;
  submitExam: () => void;
  resetExam: () => void;
  getResults: () => any;
}

const ExamContext = createContext<ExamContextType | undefined>(undefined);

export const ExamProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<
    Record<number, number>
  >({});
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [isExamActive, setIsExamActive] = useState(false);
  const [isExamFinished, setIsExamFinished] = useState(false);
  const [examClass, setExamClass] = useState<string>("");

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (isExamActive && remainingSeconds > 0) {
      timer = setInterval(() => {
        setRemainingSeconds((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            submitExam();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [isExamActive, remainingSeconds]);

  // FIXED: Added className parameter to the implementation
  const startExam = (fetchedQuestions: Question[], className: string) => {
    setQuestions(fetchedQuestions);
    setExamClass(className); // Now className is defined
    setCurrentIndex(0);
    setSelectedAnswers({});
    setRemainingSeconds(fetchedQuestions.length * 60);
    setIsExamActive(true);
    setIsExamFinished(false);
  };

  const selectAnswer = (optionIndex: number) => {
    setSelectedAnswers((prev) => ({ ...prev, [currentIndex]: optionIndex }));
  };

  const nextQuestion = () => {
    if (currentIndex < questions.length - 1)
      setCurrentIndex((prev) => prev + 1);
  };

  const prevQuestion = () => {
    if (currentIndex > 0) setCurrentIndex((prev) => prev - 1);
  };

  const submitExam = useCallback(() => {
    setIsExamActive(false);
    setIsExamFinished(true);
  }, []);

  const resetExam = () => {
    setQuestions([]);
    setCurrentIndex(0);
    setSelectedAnswers({});
    setRemainingSeconds(0);
    setIsExamActive(false);
    setIsExamFinished(false);
  };

  const getResults = () => {
    let correct = 0;
    let attempted = 0;
    Object.keys(selectedAnswers).forEach((key) => {
      const idx = parseInt(key, 10);
      if (
        questions[idx] &&
        selectedAnswers[idx] === questions[idx].correctIndex
      ) {
        correct++;
      }
      attempted++;
    });
    const total = questions.length;
    const unattempted = total - attempted;
    const wrong = attempted - correct;
    const percentage = total === 0 ? 0 : (correct / total) * 100;

    return {
      total,
      attempted,
      unattempted,
      correct,
      wrong,
      percentage,
      score: correct,
    };
  };

  return (
    <ExamContext.Provider
      value={{
        questions,
        currentIndex,
        selectedAnswers,
        remainingSeconds,
        isExamActive,
        isExamFinished,
        examClass,
        startExam,
        selectAnswer,
        nextQuestion,
        prevQuestion,
        submitExam,
        resetExam,
        getResults,
      }}
    >
      {children}
    </ExamContext.Provider>
  );
};

export const useExam = () => {
  const context = useContext(ExamContext);
  if (!context) throw new Error("useExam must be used within an ExamProvider");
  return context;
};
